// frontend/src/components/book/atelier/store/BookStoreProvider.js
//
// Branche les deux stores sur React : DEUX contextes d'etat (livre / interface)
// et DEUX contextes d'action, comme demande. Un composant qui ne fait que
// declencher une action (un bouton) s'abonne au contexte d'action, STABLE, et
// ne se re-rend pas a chaque changement d'etat.
//
// Porte aussi trois choses transverses :
//  - les messages flottants (toasts) avec leur bouton "Annuler" ;
//  - Ctrl+Z / Ctrl+Maj+Z (Ctrl+Y) — ignore dans un champ de saisie, qui garde
//    son propre annuler natif ;
//  - la mise en route du moteur de sauvegarde (useSaveEngine), et sa
//    protection : on previent avant de quitter la page avec des modifications
//    non enregistrees, et on ecrit des que l'onglet passe en arriere-plan.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore
} from 'react';
import { useTranslation } from 'react-i18next';
import { createStore } from './createStore';
import { bookReducer, createInitialBookState, BOOK_ACTIONS } from './bookReducer';
import { uiReducer, createInitialUiState, UI_ACTIONS } from './uiReducer';
import { createSaveEngine } from './saveEngine';

const BookStateContext = createContext(null);
const BookActionContext = createContext(null);
const UiStateContext = createContext(null);
const UiActionContext = createContext(null);

const isEditableTarget = (element) => Boolean(element) && (
  element.isContentEditable
  || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
);

export function BookStoreProvider({ children }) {
  const { t } = useTranslation('atelier');
  const tRef = useRef(t);
  tRef.current = t;

  const storesRef = useRef(null);
  if (!storesRef.current) {
    storesRef.current = {
      bookStore: createStore(bookReducer, createInitialBookState()),
      uiStore: createStore(uiReducer, createInitialUiState())
    };
  }
  const { bookStore, uiStore } = storesRef.current;

  const bookState = useSyncExternalStore(bookStore.subscribe, bookStore.getState);
  const uiState = useSyncExternalStore(uiStore.subscribe, uiStore.getState);

  // --- messages flottants -------------------------------------------------
  const toastActionsRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    toastActionsRef.current.delete(id);
    uiStore.dispatch({ type: UI_ACTIONS.TOAST_DISMISS, id });
  }, [uiStore]);

  const pushToast = useCallback(({ id, message, actionLabel, onAction, persistent = false, extraActions = null }) => {
    const toastId = id || `toast-${Date.now()}`;
    if (onAction) toastActionsRef.current.set(toastId, onAction);
    else toastActionsRef.current.delete(toastId);
    uiStore.dispatch({
      type: UI_ACTIONS.TOAST_PUSH,
      toast: { id: toastId, message, actionLabel: onAction ? actionLabel : null, persistent, extraActions }
    });
    return toastId;
  }, [uiStore]);

  const runToastAction = useCallback((id) => {
    const action = toastActionsRef.current.get(id);
    dismissToast(id);
    if (action) action();
  }, [dismissToast]);

  // --- annuler / retablir ---------------------------------------------------
  const undoRef = useRef(null);
  const redoRef = useRef(null);

  const undo = useCallback(() => {
    const entry = bookStore.getState().history.past.slice(-1)[0];
    if (!entry) return;
    bookStore.dispatch({ type: BOOK_ACTIONS.UNDO });
    const tt = tRef.current;
    pushToast({
      id: 'history',
      message: entry.label ? tt('store.toasts.undone', { label: entry.label }) : tt('store.toasts.undoneGeneric'),
      actionLabel: tt('store.toasts.redo'),
      onAction: () => redoRef.current()
    });
  }, [bookStore, pushToast]);

  const redo = useCallback(() => {
    const entry = bookStore.getState().history.future.slice(-1)[0];
    if (!entry) return;
    bookStore.dispatch({ type: BOOK_ACTIONS.REDO });
    const tt = tRef.current;
    pushToast({
      id: 'history',
      message: entry.label ? tt('store.toasts.redone', { label: entry.label }) : tt('store.toasts.redoneGeneric'),
      actionLabel: tt('store.toasts.undo'),
      onAction: () => undoRef.current()
    });
  }, [bookStore, pushToast]);

  undoRef.current = undo;
  redoRef.current = redo;

  // Toute action du livre passe par ici : l'heure est posee (le reducer est
  // pur), et un geste marque `meta.toast` annonce son "Annuler".
  const dispatch = useCallback((action) => {
    let stamped = action;
    if (action.type === BOOK_ACTIONS.EDIT_PAGE || action.type === BOOK_ACTIONS.EDIT_PAGES || action.type === BOOK_ACTIONS.EDIT_COVER || (action.type === BOOK_ACTIONS.LOAD && action.step)) {
      if (action.type === BOOK_ACTIONS.LOAD) {
        stamped = { ...action, step: { ...action.step, at: action.step.at ?? Date.now() } };
      } else {
        stamped = { ...action, meta: { ...action.meta, at: action.meta?.at ?? Date.now() } };
      }
    }
    const before = bookStore.getState();
    bookStore.dispatch(stamped);
    const changed = bookStore.getState() !== before;
    const label = stamped.meta?.label || stamped.step?.label;
    if (changed && label && (stamped.meta?.toast || stamped.step)) {
      pushToast({
        id: 'history',
        message: label,
        actionLabel: tRef.current('store.toasts.undo'),
        onAction: () => undoRef.current(),
        // Action supplementaire facultative (ex. « Changer » la photo choisie automatiquement).
        extraActions: stamped.meta?.toastExtra ? [stamped.meta.toastExtra] : null
      });
    }
  }, [bookStore, pushToast]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      if (key === 'y' || event.shiftKey) redoRef.current(); else undoRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const bookActions = useMemo(() => ({
    dispatch, undo, redo, pushToast, dismissToast, runToastAction, bookStore
  }), [dispatch, undo, redo, pushToast, dismissToast, runToastAction, bookStore]);

  const uiActions = useMemo(() => ({
    dispatch: uiStore.dispatch,
    set: (key, value) => uiStore.dispatch({ type: UI_ACTIONS.SET, key, value }),
    setMany: (patch) => uiStore.dispatch({ type: UI_ACTIONS.SET_MANY, patch }),
    uiStore
  }), [uiStore]);

  return (
    <BookStateContext.Provider value={bookState}>
      <BookActionContext.Provider value={bookActions}>
        <UiStateContext.Provider value={uiState}>
          <UiActionContext.Provider value={uiActions}>
            {children}
          </UiActionContext.Provider>
        </UiStateContext.Provider>
      </BookActionContext.Provider>
    </BookStateContext.Provider>
  );
}

function useRequired(context, name) {
  const value = useContext(context);
  if (value === null) throw new Error(`${name} doit etre utilise sous <BookStoreProvider>.`);
  return value;
}

/** Etat du livre : pages, couverture, historique, statut d'enregistrement. */
export const useBookState = () => useRequired(BookStateContext, 'useBookState');
/** Actions du livre (STABLE) : dispatch, undo, redo, toasts. */
export const useBookActions = () => useRequired(BookActionContext, 'useBookActions');
/** Etat d'interface : selection, navigation, panneau, fenetres, toasts. */
export const useUiState = () => useRequired(UiStateContext, 'useUiState');
/** Actions d'interface (STABLE). */
export const useUiActions = () => useRequired(UiActionContext, 'useUiActions');

/**
 * [valeur, setter] pour UN champ de l'etat d'interface. Le setter accepte une
 * valeur ou une fonction (comme useState) et garde la meme identite.
 */
export function useUiField(key) {
  const ui = useUiState();
  const { set } = useUiActions();
  const setter = useCallback((value) => set(key, value), [set, key]);
  return [ui[key], setter];
}

/**
 * Demarre le moteur de sauvegarde sur le store du livre. `io` peut changer a
 * chaque rendu (fermetures fraiches) : le moteur le lit toujours a jour.
 */
export function useSaveEngine(io) {
  const { bookStore } = useBookActions();
  const ioRef = useRef(io);
  ioRef.current = io;
  const engineRef = useRef(null);

  if (!engineRef.current) {
    engineRef.current = createSaveEngine({
      store: bookStore,
      io: {
        writePage: (...args) => ioRef.current.writePage(...args),
        writeCover: (...args) => ioRef.current.writeCover(...args),
        layoutsBySlug: () => ioRef.current.layoutsBySlug(),
        afterPageSaved: (...args) => ioRef.current.afterPageSaved && ioRef.current.afterPageSaved(...args),
        afterCoverSaved: (...args) => ioRef.current.afterCoverSaved && ioRef.current.afterCoverSaved(...args)
      }
    });
  }
  const engine = engineRef.current;

  useEffect(() => {
    const unsubscribe = bookStore.subscribe(() => engine.notify());

    // Quitter avec des modifications pas encore parties : on previent (le
    // navigateur affiche sa propre phrase), et on tente d'ecrire quand meme.
    const onBeforeUnload = (event) => {
      if (!engine.hasPending()) return;
      engine.flush();
      event.preventDefault();
      event.returnValue = '';
    };
    // Onglet cache / application mise en arriere-plan (mobile) : on ecrit tout
    // de suite, c'est souvent la derniere occasion.
    const onVisibility = () => { if (document.visibilityState === 'hidden') engine.flush(); };
    const onOnline = () => engine.retryNow();

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      unsubscribe();
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      // Sortie de l'atelier (navigation interne) : on rend tout ce qui reste.
      engine.flush();
    };
  }, [bookStore, engine]);

  return engine;
}
